import React, { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { getCrmContacts, updateCrmStage } from '../services/api';
import useApiList from '../hooks/useApiList';
import ContactRecord from './records/ContactRecord';

const TYPE_BADGE = { Scout:'badge badge-blue', Player:'badge badge-green', Agent:'badge badge-amber', Club:'badge badge-gray' };
const ACTIVATION_BADGE = a => a === 'Activated' ? 'badge badge-green' : 'badge badge-gray';

const STAGE_LABELS = {
  lead:'Lead', outreach:'Outreach', engaged:'Engaged',
  demo_scheduled:'Demo scheduled', onboarding:'Onboarding',
  onboarded:'Onboarded', activated:'Activated',
  app_downloaded:'App downloaded', profile_created:'Profile created',
  club_connected:'Club connected', registered:'Registered',
  profile_complete:'Profile complete', evaluation_purchased:'Eval purchased',
};

const ROLE_FILTERS = ['All','Scout','Player','Agent','Club'];

const importContacts = async (token, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch('/api/contacts/import', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Import failed');
  return data;
};

export default function Contacts({ toast }) {
  const { token } = useApp();
  const [roleFilter, setRoleFilter]     = useState('All');
  const [selectedContact, setSelectedContact] = useState(null);
  const [importing, setImporting]       = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef();

  const { rows, pagination, loading, error, page, search, setSearch, query, submitSearch, clearSearch, goToPage, reload } =
    useApiList(getCrmContacts, token, roleFilter !== 'All' ? { type: roleFilter } : {});

  const moveStage = async (e, contact, stage) => {
    e.stopPropagation();
    try {
      const type = contact.type === 'Club' ? 'club' : 'user';
      await updateCrmStage(token, contact.id, type, stage);
      toast?.(`Stage updated to ${STAGE_LABELS[stage] || stage}`);
      reload();
    } catch (e) { toast?.(e.message, 'error'); }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await importContacts(token, file);
      setImportResult(result.data || result);
      toast?.(`Import complete — ${(result.data || result).imported} contacts added`);
      reload();
    } catch (err) {
      toast?.(err.message, 'error');
    } finally {
      setImporting(false);
      fileRef.current.value = '';
    }
  };

  if (selectedContact) {
    return (
      <ContactRecord
        contactId={selectedContact}
        onBack={() => setSelectedContact(null)}
        toast={toast}
      />
    );
  }

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total contacts</div><div className="stat-value">{pagination?.total ?? '—'}</div><div className="stat-sub">All roles</div></div>
        <div className="stat-card"><div className="stat-label">Activated</div><div className="stat-value">{rows.filter(r=>r.activationStatus==='Activated').length}</div><div className="stat-sub stat-up">This page</div></div>
        <div className="stat-card"><div className="stat-label">Not activated</div><div className="stat-value">{rows.filter(r=>r.activationStatus!=='Activated').length}</div><div className="stat-sub">This page</div></div>
        <div className="stat-card"><div className="stat-label">Pages</div><div className="stat-value">{pagination?.totalPages ?? '—'}</div><div className="stat-sub">Paginated</div></div>
      </div>

      <div className="filter-pills" style={{ marginBottom:12 }}>
        {ROLE_FILTERS.map(f => (
          <button key={f} className={`pill ${roleFilter===f?'active':''}`} onClick={() => setRoleFilter(f)}>{f}</button>
        ))}
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:12, alignItems:'center' }}>
        <form className="toolbar" style={{ flex:1, margin:0 }} onSubmit={e => { e.preventDefault(); submitSearch(search); }}>
          <input className="search-input" placeholder="Search contacts by name or email…" value={search} onChange={e => setSearch(e.target.value)} />
          <button type="submit" className="btn btn-ghost">Search</button>
          {query && <button type="button" className="btn btn-ghost" onClick={clearSearch}>Clear</button>}
        </form>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" style={{ display:'none' }} onChange={handleFileChange} />
        <button
          className="btn btn-primary"
          onClick={() => fileRef.current.click()}
          disabled={importing}
          style={{ whiteSpace:'nowrap' }}
        >
          {importing ? 'Importing…' : '⬆ Import contacts'}
        </button>
      </div>

      {importResult && (
        <div className="card" style={{ padding:'12px 16px', marginBottom:16, borderColor:'var(--accent)' }}>
          <div style={{ fontSize:13, color:'var(--text-primary)' }}>
            ✅ <strong>{importResult.imported}</strong> imported &nbsp;·&nbsp;
            <strong>{importResult.skipped}</strong> skipped (duplicates) &nbsp;·&nbsp;
            <strong>{importResult.errors?.length || 0}</strong> errors
            <button className="btn btn-ghost" style={{ fontSize:11, marginLeft:12 }} onClick={() => setImportResult(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {error && <div className="card" style={{ padding:16, marginBottom:16, borderColor:'var(--danger)' }}><p style={{ color:'var(--danger)', fontSize:13 }}>{error}</p></div>}

      <div className="card" style={{ padding: 0 }}>
        <div className="card-header" style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
          <span className="card-title">All contacts</span>
          {pagination && <span style={{ fontSize:12, color:'var(--text-dim)' }}>Page {pagination.page} of {pagination.totalPages} · {pagination.total} total</span>}
        </div>
        {loading ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading contacts…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>No contacts found</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Type</th>
                  <th>Pipeline stage</th>
                  <th>Activation</th>
                  <th>Source</th>
                  <th>First action</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(c => (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedContact(c.id)}
                    style={{ cursor:'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background=''}
                  >
                    <td>
                      <div style={{ fontWeight:500, color:'var(--accent)' }}>{c.name || `${c.firstName} ${c.lastName}`}</div>
                      <div style={{ fontSize:11, color:'var(--text-dim)' }}>{c.email}</div>
                      {c.affiliatedClub && <div style={{ fontSize:11, color:'var(--text-dim)' }}>↳ {c.affiliatedClub}</div>}
                    </td>
                    <td><span className={TYPE_BADGE[c.type] || 'badge badge-gray'}>{c.type}</span></td>
                    <td>
                      <select
                        value={c.pipelineStage || 'lead'}
                        onChange={e => moveStage(e, c, e.target.value)}
                        onClick={e => e.stopPropagation()}
                        style={{ background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:12, padding:'3px 8px', cursor:'pointer' }}
                      >
                        {Object.entries(STAGE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                    <td><span className={ACTIVATION_BADGE(c.activationStatus)}>{c.activationStatus || 'Not Activated'}</span></td>
                    <td style={{ color:'var(--text-muted)', fontSize:12 }}>{c.leadSource || '—'}</td>
                    <td style={{ color:'var(--text-dim)', fontSize:12 }}>{c.firstActionDate ? new Date(c.firstActionDate).toLocaleDateString() : '—'}</td>
                    <td style={{ color:'var(--text-dim)', fontSize:12 }}>{new Date(c.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:16 }}>
          <button className="btn btn-ghost" disabled={page<=1} onClick={() => goToPage(page-1)}>← Prev</button>
          <span style={{ padding:'8px 14px', fontSize:13, color:'var(--text-muted)' }}>Page {page} / {pagination.totalPages}</span>
          <button className="btn btn-ghost" disabled={page>=pagination.totalPages} onClick={() => goToPage(page+1)}>Next →</button>
        </div>
      )}
    </div>
  );
}
